<template>
    <div v-if="stek" class="omnibox-container">
        <!-- Fake Input Trigger -->
        <div class="fake-omnibox-input" @click="openSearch">
            <i class="pi pi-search omnibox-icon" />
            <span class="placeholder">Search...</span>
            <span class="keyboard-shortcut">⌘K</span>
        </div>

        <!-- Search Modal -->
        <Dialog
            v-model:visible="showSearchModal"
            modal
            :dismissableMask="true"
            :closable="false"
            :showHeader="false"
            class="omni-box-dialog"
            @show="onShow"
            @hide="onHide"
            :style="{ width: '800px', maxWidth: '90vw' }"
        >
            <div class="omnibox-modal-content">
                <IconField class="fluid-input">
                    <InputIcon class="pi pi-search" />
                    <AutoComplete
                        ref="omniboxInput"
                        v-model="selectedItem"
                        :suggestions="filteredItems"
                        @complete="search"
                        @item-select="onSelect"
                        optionLabel="name"
                        placeholder="Search..."
                        class="omnibox-input"
                        :delay="300"
                        :minLength="2"
                        fluid
                    >
                        <template #option="slotProps">
                            <div
                                v-if="slotProps.option.type === 'loading'"
                                class="omnibox-result-item loading-item"
                            >
                                <i class="pi pi-spinner pi-spin"></i>
                                <span>Searching...</span>
                            </div>
                            <div v-else class="omnibox-result-item">
                                <div class="item-header">
                                    <i
                                        :class="getIcon(slotProps.option.type)"
                                        class="type-icon"
                                    ></i>
                                    <span class="item-name">{{
                                        slotProps.option.name
                                    }}</span>
                                    <span class="item-type-badge">{{
                                        slotProps.option.type
                                    }}</span>
                                </div>
                                <div
                                    v-if="
                                        slotProps.option.breadcrumbs &&
                                        slotProps.option.breadcrumbs.length
                                    "
                                    class="breadcrumbs"
                                >
                                    {{
                                        slotProps.option.breadcrumbs.join(" > ")
                                    }}
                                </div>
                                <div
                                    v-if="slotProps.option.description"
                                    class="snippet"
                                >
                                    {{ slotProps.option.description }}
                                </div>
                            </div>
                        </template>
                    </AutoComplete>
                </IconField>
            </div>
        </Dialog>
    </div>
</template>

<script setup lang="ts">
import AutoComplete, {
    AutoCompleteCompleteEvent,
    AutoCompleteOptionSelectEvent,
} from "primevue/autocomplete";
import Dialog from "primevue/dialog";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import { nextTick, ref, useTemplateRef } from "vue";
import { useRouter } from "vue-router";

import { useHotKeys } from "@/composables/hotkeys";
import { useStekManager } from "@/composables/stek-manager";
import { api } from "@/lib/openapi";

const router = useRouter();
const { stek } = useStekManager();
const { addHotKey } = useHotKeys();
const selectedItem = ref();
const filteredItems = ref<{ type: string; name: string }[]>([]);
const isLoading = ref(false);
const showSearchModal = ref(false);

const omniboxInput =
    useTemplateRef<InstanceType<typeof AutoComplete>>("omniboxInput");

const openSearch = () => {
    showSearchModal.value = true;
};

const onShow = () => {
    nextTick(() => {
        // PrimeVue AutoComplete input element
        const input = omniboxInput.value?.$el.querySelector("input");
        if (input) {
            input.focus();
        }
    });
};

const onHide = () => {
    selectedItem.value = null;
    filteredItems.value = [];
};

addHotKey(
    "ControlOrMeta+k",
    (event) => {
        event.preventDefault();
        openSearch();
    },
    "Focus Search",
    "Global"
);

const search = async (event: AutoCompleteCompleteEvent) => {
    isLoading.value = true;
    filteredItems.value = [{ type: "loading", name: "Searching..." }];
    try {
        const { data } = await api.searchSearchGet({
            query: { q: event.query },
        });
        filteredItems.value = data?.results || [];
    } catch (error) {
        console.error("Search failed:", error);
        filteredItems.value = [];
    } finally {
        isLoading.value = false;
    }
};

const onSelect = (event: AutoCompleteOptionSelectEvent) => {
    const item = event.value;
    if (item.link) {
        router.push(item.link);
        // Clear selection and close modal after navigation
        selectedItem.value = null;
        showSearchModal.value = false;
    }
};

const getIcon = (type: string) => {
    switch (type) {
        case "organization":
            return "pi pi-building";
        case "stek":
            return "pi pi-sitemap";
        case "domain":
            return "pi pi-cloud";
        case "product":
            return "pi pi-box";
        case "process":
            return "pi pi-share-alt";
        case "process_task":
            return "pi pi-check-square";
        case "process_gateway":
            return "pi pi-directions";
        case "process_event":
            return "pi pi-stop-circle";
        case "role":
            return "pi pi-id-card";
        case "business_entity":
            return "pi pi-building";
        case "document":
            return "pi pi-file";
        default:
            return "pi pi-omnibox";
    }
};
</script>

<style lang="scss">
/* Modal and AutoComplete Overrides - Global because Dialog is teleported */
.omni-box-dialog {
    --p-dialog-content-padding: 0;
}
</style>

<style scoped lang="scss">
.omnibox-container {
    display: flex;
    align-items: center;
}

.fake-omnibox-input {
    width: 100%;
    height: 35px;
    border: 1px solid var(--surface-border);
    background: var(--surface-card);
    border-radius: 6px;
    padding: 0 0.75rem;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    position: relative;
    color: var(--text-color);

    &:hover {
        border-color: var(--primary-color);
        background: var(--surface-hover);
    }

    .omnibox-icon {
        margin-right: 0.5rem;
        color: var(--text-color-secondary);
    }

    .placeholder {
        color: var(--text-color-secondary);
        flex: 1;
        padding-right: 2em;
    }

    .keyboard-shortcut {
        font-size: 0.75rem;
        border: 1px solid var(--surface-border);
        border-radius: 4px;
        padding: 0.1rem 0.4rem;
        color: var(--text-color-secondary);
        background: var(--surface-ground);
        font-weight: 600;
    }
}

.omnibox-modal-content {
    background: var(--surface-overlay);

    :deep(.omnibox-input) {
        .p-autocomplete-input {
            width: 100%;
            padding: 1rem;
            font-size: 1rem;
            border: none;
            border-bottom: 1px solid var(--surface-border);
            border-radius: 0;
            background: transparent;

            &:focus {
                box-shadow: none;
            }
        }
    }
}

.fluid-input {
    width: 100%;
}

.omnibox-result-item {
    width: 100%;
    padding: 0.75rem 1.25rem;
    display: flex;
    flex-direction: column;
    border-bottom: 1px solid var(--surface-border);

    .item-header {
        display: flex;
        align-items: center;
        margin-bottom: 0.25rem;

        .type-icon {
            margin-right: 0.75rem;
            color: var(--primary-color);
            font-size: 1.1rem;
        }

        .item-name {
            font-weight: 600;
            font-size: 1rem;
        }

        .item-type-badge {
            margin-left: auto;
            font-size: 0.65rem;
            background-color: var(--surface-100);
            color: var(--text-color-secondary);
            padding: 0.1rem 0.4rem;
            border-radius: 4px;
            text-transform: uppercase;
            font-weight: bold;
        }
    }

    .breadcrumbs {
        font-size: 0.8rem;
        color: var(--text-color-secondary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        padding-left: 1.85rem;
    }

    .snippet {
        font-size: 0.8rem;
        color: var(--text-color-secondary);
        font-style: italic;
        margin-top: 0.25rem;
        padding-left: 1.85rem;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    &.loading-item {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        gap: 0.75rem;
        color: var(--text-color-secondary);
        padding: 2rem;
        font-style: italic;
        border-bottom: none;
    }
}
</style>
